<script lang="ts">
  // @ts-nocheck
  import { onMount, onDestroy } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faSave, faTimes, faArrowDown, faChevronDown, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
  import { Button } from 'flowbite-svelte';
  import CustomImage from '../../common/CustomImage.svelte';
  import { createGrid } from 'ag-grid-community';
  import type { GridApi } from 'ag-grid-community';
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-quartz.css';
  import { theme } from '../../../services/stores/theme-store.js';
  import { projectStructureStore, studyExtractStore, documentExtractStore, documentExtractFullStore } from './stores.js';
  import { userStore } from '../../../services/stores/user-store.js';
  import { documentExtractTableTo2DArray, studyTableTo2DArray, arrayToStudyTable, arrayToReference, updateTargetCell } from './target-table-processing.js';
  import { convertExtractToWorking, getCurrentVersion, updateCellValue, addRow, addColumn, removeRow, removeColumn, resetToInitial, pivotTable, workingTableTo2DArray } from './source-table-processing.js';
  import { addTargetRow, removeTargetRows, reorderTargetColumns } from './target-table-processing.js';
  import { saveTargetExtract } from '../data/target-data-handler.js';
  import { 
    validateSourceAgainstTarget, 
    getValidationSummary,
    matchOnlySync,
    matchAllSync,
    getSyncSummary,
    buildSourceKeyMapping,
    buildTargetKeyMapping,
    filterRowsBySelection,
    matchOnlySyncWithKeys,
    matchAllSyncWithKeys,
    matchSelectedSyncWithKeys,
    matchOnlySyncTargetToTarget,
    matchAllSyncTargetToTarget,
    matchSelectedSyncTargetToTarget
  } from './table-mapping.js';
  import { ColumnHeaderForTarget, ColumnHeaderForSource } from './headerComponents.js';
  import { loadDataForMode as loadDataForModeExternal, type FlowMode, type DataLoadingContext } from './data-loading.js';
  import { handleMatchSelected as handleMatchSelectedExternal, handleDocumentStudySelected as handleDocumentStudySelectedExternal, handleDocumentStudyMatching as handleDocumentStudyMatchingExternal, handleDocumentStudyAll as handleDocumentStudyAllExternal, getSelectedRowsFromGrid } from './replacementFunctions.js';
  import { preprocessTableOnce } from './source-table-processing.js';
  import { saveWorkingDocument } from '../data/extract-data-handler.js';
  // @ts-ignore
  // Svelte component resolved by bundler configuration
  import SourceTableEditor from './SourceTableEditor.svelte';
  // @ts-ignore
  // Svelte component resolved by bundler configuration
  import TopicGrid from './TopicGrid.svelte';
  // Grouper editing is now handled by separate GrouperEditor component
  // Only keeping grouper utility imports needed for regular table columns that reference grouper
  import { getDisplayName, getGrouperIds } from './grouper-utils.js';
  import { getCellEditorConfig, getLookupValueGetterSetter, getLookupCellRenderer } from './cell-editor-config.js';
  import { createCellWithIndicator, DEFAULT_CURRENT_DOCUMENT_ID } from '../indicator-utils.js';
  import { logInfo, logError, logWarning } from "../../../services/logging/log-handler.js";
  
  // Props
  export let show: boolean = false;
  export let pageNumber: number = 5;
  export let topicId: string = 'baseline';
  export let tableId: string = 'age';
  export let referenceId: string = ''; // Optional: when provided, component operates in reference mode
  
  // Force reactivity for show variable
  $: isVisible = show;
  
  // Detect if we're in reference mode (referenceId provided) or topic mode (topicId/tableId provided)
  $: isReferenceMode = !!referenceId && referenceId.trim() !== '';
  $: isTopicMode = !!topicId && topicId.trim() !== '' && !!tableId && tableId.trim() !== '';
  
  // Mode selection state
  let currentMode: FlowMode = 'source-document';
  
  // Grid refs (kept for backward compatibility but no longer used - grids are now handled by TopicGrid component)
  // let documentTargetGridDiv: HTMLDivElement; // No longer used - replaced by TopicGrid
  // let studyTargetGridDiv: HTMLDivElement;   // No longer used - replaced by TopicGrid
  let sourceGridApi: GridApi | null = null; // Set by SourceTableEditor via onGridReady callback
  let documentTargetGridApi: GridApi | null = null; // Used in source-document mode and document-study mode
  let studyTargetGridApi: GridApi | null = null;   // Used in source-study mode and document-study mode
  let documentTargetGridInstance: any = null; // Used in source-document mode and document-study mode
  let studyTargetGridInstance: any = null;   // Used in source-study mode and document-study mode
  
  // State
  let availableSourceTables: any[] = [];
  let selectedSourceTable: any | null = null;
  let sourceData2D: any[][] = [];
  let documentTargetData2D: any[][] = []; // Used in source-document mode and document-study mode
  let studyTargetData2D: any[][] = [];   // Used in source-study mode and document-study mode
  let originalSourceData: any[][] = [];
  let validationResult: any = null;
  let hasChanges = false;
  let syncSummary = '';
  
  // Working format data
  let workingData: any = null;
  let currentTableIndex: number = 0;
  let isUpdatingCell = false; // Flag to prevent reactive updates during cell editing
  let isUpdatingDocumentTarget = false; // Flag to prevent reactive updates during document target operations (for source-document mode and document-study mode)
  let isUpdatingStudyTarget = false; // Flag to prevent reactive updates during study target operations (for source-study mode and document-study mode)
  let sourceGridInitialized = false; // Flag to track if source grid has been initialized
  let documentTargetGridInitialized = false; // Flag to track if document target grid has been initialized (for source-document mode and document-study mode)
  let studyTargetGridInitialized = false; // Flag to track if study target grid has been initialized (for source-study mode and document-study mode)
  let lastSelectedSourceTable: any | null = null; // Track last selected source table to detect changes
  let lastEffectiveTableId: string = ''; // Track last effectiveTableId to detect changes
  
  // Pivot state - no longer needed since we always pivot
  
  // effectiveTableId is the target we're working with (passed as prop or detected)
  $: effectiveTableId = (() => {
    // In reference mode, effectiveTableId should be the referenceId
    if (isReferenceMode && referenceId) {
      return referenceId;
    }
    
    
    // If tableId prop is provided, use it (it's the target, e.g., "age", "gender", "subgroups")
    if (tableId && tableId.trim()) {
      return tableId;
    }
    
    // If no tableId prop, try to detect from selectedSourceTable
    if (selectedSourceTable && selectedSourceTable.work && Array.isArray(selectedSourceTable.work) && selectedSourceTable.work.length > 0) {
      const detected = selectedSourceTable.work[0].target;
      return detected;
    }
    
    return ''; // Fallback to empty string
  })();
  
  // Compute display name for table title
  $: tableDisplayName = (() => {
    if (isReferenceMode && referenceId && $projectStructureStore) {
      const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
      return referenceStructure?.label || referenceId;
    }
          if (isTopicMode && topicId && effectiveTableId && $projectStructureStore) {
      // Grouper editing is handled by separate GrouperEditor component
      const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
      const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
      return tableStructure?.label || effectiveTableId;
    }
    return effectiveTableId || '';
  })();
  
  // Selection state
  let selectedColumns: Set<number> = new Set();
  let selectedRows: Set<number> = new Set();
  let selectedDocumentTargetRows: Set<number> = new Set(); // For document target grid row selection (source-document mode and document-study mode)
  let selectedStudyTargetRows: Set<number> = new Set(); // For study target grid row selection (source-study mode and document-study mode)
  
  // Row ID mappings for target grids (rowIndex -> rowId)
  let studyTargetRowIdMap: Map<number, string> = new Map();
  let documentTargetRowIdMap: Map<number, string> = new Map();
  let showColumnAddDropdown = false;
  
  // Current document ID for source indicator logic (for study target grid indicators)
  // This should match the current document being viewed
  const currentDocumentID = DEFAULT_CURRENT_DOCUMENT_ID;
  
  // Computed: Get grouper data for study target grid
  $: studyTargetGrouperData = (() => {
    if (!$studyExtractStore || !topicId) return null;
    const topicData = $studyExtractStore.topics?.find((t: any) => t.id === topicId);
    return topicData?.grouper || null;
  })();
  
  // Computed: Get grouper data for document target grid
  $: documentTargetGrouperData = (() => {
    if (!$documentExtractFullStore || !topicId) return null;
    const topicData = $documentExtractFullStore.topics?.find((t: any) => t.id === topicId);
    return topicData?.grouper || null;
  })();
  
  // Computed: Get table structure for study target grid
  $: studyTargetTableStructure = (() => {
    if (!$projectStructureStore || !topicId || !effectiveTableId) return null;
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) return null;
    return topicStructure.tables?.find((t: any) => t.id === effectiveTableId) || null;
  })();
  
  // Computed: Get table data for study target grid
  $: studyTargetTableData = (() => {
    if (isReferenceMode || !$studyExtractStore || !topicId || !effectiveTableId) return null;
    const topicData = $studyExtractStore.topics?.find((t: any) => t.id === topicId);
    if (!topicData) return null;
    return topicData.tables?.find((t: any) => t.id === effectiveTableId) || null;
  })();
  
  // Computed: Get table structure for document target grid
  $: documentTargetTableStructure = (() => {
    if (!$projectStructureStore || !topicId || !effectiveTableId) return null;
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) return null;
    return topicStructure.tables?.find((t: any) => t.id === effectiveTableId) || null;
  })();
  
  // Computed: Get table data for document target grid
  $: documentTargetTableData = (() => {
    if (isReferenceMode || !$documentExtractFullStore || !topicId || !effectiveTableId) return null;
    const topicData = $documentExtractFullStore.topics?.find((t: any) => t.id === topicId);
    if (!topicData) return null;
    return topicData.tables?.find((t: any) => t.id === effectiveTableId) || null;
  })();
  
  // Helper function to get sourceInfo from study extract store for a specific cell
  function getSourceInfoForCell(rowIndex: number, columnId: string): { sourceType: string; sourceID?: string; sourceName?: string; sourceDate?: string } | null | undefined {
    if (!$studyExtractStore || !topicId || !effectiveTableId) {
      return null;
    }
    
    // Get row ID from mapping
    let rowId = studyTargetRowIdMap.get(rowIndex);
    
    // Find the cell in the study extract store
    const topicData = $studyExtractStore.topics?.find((t: any) => t.id === topicId);
    if (!topicData) {
      return null;
    }
    
    const tableData = topicData.tables?.find((t: any) => t.id === effectiveTableId);
    if (!tableData) {
      return null;
    }
    
    if (!rowId) {
      rowId = tableData.rows?.[rowIndex]?.id;
    }
    if (!rowId) {
      return null;
    }
    
    const row = tableData.rows?.find((r: any) => r.id === rowId);
    if (!row || !row.cells) {
      return null;
    }
    
    const cell = row.cells.find((c: any) => c.id === columnId);
    if (!cell || !cell.sourceInfo) {
      return null;
    }
    
    return cell.sourceInfo;
  }
  
  // Grouper editor state (no longer using popup, using inline grid)
  
  // Column popup state moved to SourceTableEditor
  
  // Button state logic
  $: columnAddEnabled = selectedColumns.size === 0;
  $: columnAddIsDropdown = selectedColumns.size === 1;
  $: columnRemoveEnabled = selectedColumns.size > 0;
  $: columnEditEnabled = selectedColumns.size === 1;
  
  $: rowRemoveEnabled = selectedRows.size > 0;
  
  // Replace button state logic
  // Check if at least one column matches between source and target
  // This checks both label matching (from validation) and key-based mapping
  $: hasColumnMatch = (() => {
    // First check validation result (label-based matching)
    if (validationResult && validationResult.columnValidation) {
      const matchedColumns = Array.from(validationResult.columnValidation.values()).filter(v => v === true);
      if (matchedColumns.length > 0) {
        return true;
      }
    }
    
    // If no label matches, check if we have key-based mappings
    // This is important for references where labels may differ but keys match
    if (sourceData2D.length > 0) {
      let targetData: any[][] = [];
      if (currentMode === 'source-document') {
        targetData = documentTargetData2D;
      } else if (currentMode === 'source-study') {
        targetData = studyTargetData2D;
      }
      
      if (targetData.length > 0 && workingData && currentTableIndex >= 0 && effectiveTableId) {
        try {
          // Build key mappings to check if any columns match by key
          const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
          const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
          const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
          
          // Check if any source column has a matching target column by key
          const sourceHeaders = sourceData2D[0];
          for (const sourceHeader of sourceHeaders) {
            const sourceKey = sourceKeyMapping.get(sourceHeader);
            if (sourceKey) {
              // Check if any target header maps to the same key
              for (const targetHeader of targetHeaders) {
                const targetKey = targetKeyMapping.get(targetHeader);
                if (targetKey === sourceKey) {
                  return true; // Found a match by key
                }
              }
            }
          }
        } catch (error) {
          logError(error, "TopicTableEditor", undefined, { message: 'Error checking key-based column matching' });
        }
      }
    }
    
    return false;
  })();
  
  $: selectedButtonEnabled = selectedRows.size > 0 && hasColumnMatch; // For source-document and source-study modes
  $: matchingButtonEnabled = hasColumnMatch; // For Matching and All buttons
  $: documentStudySelectedButtonEnabled = selectedDocumentTargetRows.size > 0; // For document-study mode
  
  // Debug logging for button state
  $: if (show) {
  }
  
  // Target table button state logic
  $: documentTargetRowRemoveEnabled = selectedDocumentTargetRows.size > 0; // For document target grid (source-document mode and document-study mode)
  $: studyTargetRowRemoveEnabled = selectedStudyTargetRows.size > 0; // For study target grid (source-study mode and document-study mode)
  
  // Load available source tables from documentExtract for all pages
  $: if (show && $documentExtractStore?.tables) {
    
    // Show all tables from all pages, not just the current page, but only with status valid or none
    availableSourceTables = ($documentExtractStore.tables || []).filter((t: any) => !t.status || t.status === 'valid');
    
    // Auto-select the first table if none is selected
    if (availableSourceTables.length > 0 && !selectedSourceTable) {
      selectedSourceTable = availableSourceTables[0];
    }
  }

  // When a source table is selected, ensure it is preprocessed if needed, then persist
  $: if (show && selectedSourceTable && $documentExtractStore) {
    try {
      const key = `${selectedSourceTable.page}::${selectedSourceTable.name}`;
      if (!selectedSourceTable.status) {
        // Find and preprocess this table only
        const ix = $documentExtractStore.tables.findIndex((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
        if (ix >= 0) {
          console.group(`[SOURCE-PREPROCESS] Preprocessing ${key}`);
          const processed = preprocessTableOnce($documentExtractStore.tables[ix]);
          
          // Update documentExtractStore
          const updatedDoc = JSON.parse(JSON.stringify($documentExtractStore));
          updatedDoc.tables[ix] = processed;
          documentExtractStore.set(updatedDoc);
          
          // Also ensure work section exists and update workingData (only if we have a target)
          // Compute effectiveTableId inside to avoid cyclical dependency
          const computedEffectiveTableIdForPreprocess = tableId && tableId.trim() ? tableId : '';
          if (workingData && computedEffectiveTableIdForPreprocess) {
            const workTableIx = workingData.tables.findIndex((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
            if (workTableIx >= 0) {
              // Update the table in workingData to match preprocessed version
              workingData.tables[workTableIx] = JSON.parse(JSON.stringify(processed));
              const workTable = workingData.tables[workTableIx];
              
              // Ensure work array exists
              if (!Array.isArray(workTable.work)) {
                workTable.work = [];
              }
              
              // Check if work entry for this target already exists
              const existingWorkIndex = workTable.work.findIndex((w: any) => w?.target === computedEffectiveTableIdForPreprocess);
              
              if (existingWorkIndex < 0) {
                // Create new work entry from preprocessed data
                const newWorkEntry = {
                  target: computedEffectiveTableIdForPreprocess,
                  next_row: `r${(processed.data?.rows?.length || 0) + 1}`,
                  next_col: `c${(processed.data?.columns?.length || 0) + 1}`,
                  columns: JSON.parse(JSON.stringify(processed.data?.columns || [])),
                  rows: JSON.parse(JSON.stringify(processed.data?.rows || []))
                };
                workTable.work.push(newWorkEntry);
              } else {
                // Update existing work entry verifying preprocessed data
                workTable.work[existingWorkIndex] = {
                  ...workTable.work[existingWorkIndex],
                  columns: JSON.parse(JSON.stringify(processed.data?.columns || [])),
                  rows: JSON.parse(JSON.stringify(processed.data?.rows || []))
                };
              }
              
              saveWorkingData();
            } else {
              logWarning('Table not found in workingData for page:', "TopicTableEditor", undefined, { page: selectedSourceTable.page, name: selectedSourceTable.name });
            }
          } else {
            logWarning('Skipping work section creation - workingData:', "TopicTableEditor", undefined, { workingData: !!workingData, computedEffectiveTableId: computedEffectiveTableIdForPreprocess });
          }
          
          // Persist to storage (using same name identifier as ExtractItems2)
          saveWorkingDocument('doc1', updatedDoc);
          console.groupEnd();
          // Update selection reference to the processed table
          selectedSourceTable = processed;
        }
      }
    } catch (e) {
      logError(e, "TopicTableEditor", undefined, { message: 'Error while preprocessing selected table' });
    }
  }

  // Separate reactive block to ensure work section exists after preprocessing when workingData is available
  $: if (show && selectedSourceTable && selectedSourceTable.status === 'valid' && workingData && tableId && $documentExtractStore) {
    try {
      // Compute effectiveTableId inside the block to avoid cyclical dependency
      const computedEffectiveTableId = tableId && tableId.trim() ? tableId : '';
      if (computedEffectiveTableId) {
        const key = `${selectedSourceTable.page}::${selectedSourceTable.name}`;
        // Check if this table needs a work entry
        const workTableIx = workingData.tables.findIndex((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
        if (workTableIx >= 0) {
          const workTable = workingData.tables[workTableIx];
          // Check if work entry exists for this target
          const hasWorkEntry = Array.isArray(workTable.work) && workTable.work.find((w: any) => w?.target === computedEffectiveTableId);
          if (!hasWorkEntry) {
            
            // Get the preprocessed table from documentExtractStore
            const docTable = $documentExtractStore.tables.find((t: any) => t.page === selectedSourceTable.page && t.name === selectedSourceTable.name);
            if (docTable && docTable.status === 'valid' && docTable.data) {
              // Update workingData table to match preprocessed version
              workingData.tables[workTableIx] = JSON.parse(JSON.stringify(docTable));
              const updatedTable = workingData.tables[workTableIx];
              
              // Ensure work array exists
              if (!Array.isArray(updatedTable.work)) {
                updatedTable.work = [];
              }
              
              // Create work entry from preprocessed data
              const newWorkEntry = {
                target: computedEffectiveTableId,
                next_row: `r${(updatedTable.data?.rows?.length || 0) + 1}`,
                next_col: `c${(updatedTable.data?.columns?.length || 0) + 1}`,
                columns: JSON.parse(JSON.stringify(updatedTable.data?.columns || [])),
                rows: JSON.parse(JSON.stringify(updatedTable.data?.rows || []))
              };
              updatedTable.work.push(newWorkEntry);
              saveWorkingData();
            }
          }
      }
      }
    } catch (e) {
      logError(e, "TopicTableEditor", undefined, { message: 'Error ensuring work section' });
    }
  }

  // Load or create working data when editor opens or page/topic/table/mode changes
  let lastPageNumber: number | undefined = undefined;
  let lastTopicId: string | undefined = undefined;
  let lastTableId: string | undefined = undefined;
  let lastReferenceId: string | undefined = undefined;
  let lastMode: FlowMode | undefined = undefined;
  
  $: if (show && $documentExtractStore) {
    // Only reset initialization flags if page/topic/table/reference/mode changed, not on every open/close
    const pageChanged = lastPageNumber !== pageNumber;
    const topicChanged = lastTopicId !== topicId;
    const tableChanged = lastTableId !== tableId;
    const referenceChanged = lastReferenceId !== referenceId;
    const modeChanged = lastMode !== currentMode;
    
    if (pageChanged || topicChanged || tableChanged || referenceChanged) {
      sourceGridInitialized = false;
      documentTargetGridInitialized = false;
      studyTargetGridInitialized = false;
      
      lastPageNumber = pageNumber;
      lastTopicId = topicId;
      lastTableId = tableId;
      lastReferenceId = referenceId;
    }
    
    if (modeChanged) {
      // Reset the appropriate target grid flag based on the new mode
      if (currentMode === 'source-document') {
        documentTargetGridInitialized = false;
      } else if (currentMode === 'source-study') {
        studyTargetGridInitialized = false;
      } else if (currentMode === 'document-study') {
        documentTargetGridInitialized = false;
        studyTargetGridInitialized = false;
      }
      
      lastMode = currentMode;
    }
    
    loadWorkingData();
  }

  // Load target data for source-document mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'source-document' && !documentTargetGridInitialized && !isUpdatingDocumentTarget) {
    loadDataForMode();
  }

  // Document target grid is now handled by TopicGrid component - no manual initialization needed

  // Load target data for source-study mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'source-study' && !studyTargetGridInitialized && !isUpdatingStudyTarget) {
    loadDataForMode();
  }

  // Study target grid is now handled by TopicGrid component - no manual initialization needed for source-study mode

  // Document target grid is now handled by TopicGrid component - no manual initialization needed for document-study mode

  // Study target grid is now handled by TopicGrid component - no manual initialization needed for document-study mode

  function loadWorkingData() {
    const storageKey = `workingData_${pageNumber}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        workingData = JSON.parse(stored);
        
        // Check if we need to merge additional tables from documentExtractStore
        if ($documentExtractStore?.tables && $documentExtractStore.tables.length > 0 && workingData?.tables) {
          const needsMerge = $documentExtractStore.tables.some((table: any) => {
            const exists = workingData.tables.find((wt: any) => 
              wt.page === table.page && wt.name === table.name
            );
            return !exists;
          });
          
          if (needsMerge) {
           
            // Add any missing tables
            $documentExtractStore.tables.forEach((table: any) => {
              const exists = workingData.tables.find((wt: any) => 
                wt.page === table.page && wt.name === table.name
              );
              if (!exists) {
                workingData.tables.push(JSON.parse(JSON.stringify(table))); // Deep copy
              }
            });
          }
        }
        
        // Auto-assign keys for grouper reference columns even for loaded data
        assignGrouperKeys();
        // Note: Work sections are created in the preprocessing block when a table is selected
        saveWorkingData();
      } catch (error) {
        logError(error, "TopicTableEditor", undefined, { message: "Error parsing stored working data" });
        createWorkingData();
      }
    } else {
      createWorkingData();
    }
  }

  function createWorkingData() {
    // Check if we already have working data structure
    if ($documentExtractStore?.tables?.[0]?.data && $documentExtractStore?.tables?.[0]?.work) {
      workingData = $documentExtractStore;
    } else {
      // Convert extract format to working format
      workingData = convertExtractToWorking($documentExtractStore);
    }
    
    // Merge additional tables from documentExtractStore if they exist
    if ($documentExtractStore?.tables && $documentExtractStore.tables.length > 1 && workingData?.tables) {
      // Add any tables from documentExtractStore that aren't in workingData
      $documentExtractStore.tables.forEach((table: any) => {
        const exists = workingData.tables.find((wt: any) => 
          wt.page === table.page && wt.name === table.name
        );
        if (!exists) {
          workingData.tables.push(table);
        }
      });
    }
    
    // Note: Work sections are created in the preprocessing block when a table is selected
    
    // Auto-assign keys for grouper reference columns
    assignGrouperKeys();
    
    saveWorkingData();
  }
  
  function assignGrouperKeys() {
    // Skip grouper key assignment for references (they don't have grouper)
    if (isReferenceMode) {
      return;
    }
    
    if (!workingData || !$projectStructureStore || !topicId || !effectiveTableId) {
      return;
    }
        
    // Find the target table structure
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) {
      return;
    }
    
    const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
    if (!tableStructure) {
      return;
    }
    
    const grouperStructure = topicStructure.grouper;
    if (!grouperStructure || !grouperStructure.levels || grouperStructure.levels.length === 0) {
      return;
    }
    
    const grouperLevelId = grouperStructure.levels[0].id; // e.g., "group"
    
    // Find grouper reference columns in the table structure
    const grouperColumns = tableStructure.columns?.filter((col: any) => col.refTable === 'grouper') || [];
    
    workingData.tables?.forEach((table: any, tableIndex: number) => {
      // Check if table has data in the working format (data/work)
      const tableData = table.data || (table.work ? table.work : null);
      
      if (!tableData || !Array.isArray(tableData)) {
        return;
      }
      
      tableData.forEach((version: any, versionIndex: number) => {
        if (!version.columns) {
          return;
        }
        
        // Match each column in working data to grouper columns by label
        version.columns.forEach((workingCol: any, colIndex: number) => {
          const matchedGrouperCol = grouperColumns.find((gc: any) => 
            gc.label?.toLowerCase().trim() === workingCol.label?.toLowerCase().trim()
          );
          
          if (matchedGrouperCol) {
            // This is a grouper reference column - assign the grouper level ID as the key
            const oldKey = workingCol.key;
            workingCol.key = grouperLevelId;
            workingCol.match = true;
          }
        });
      });
    });
  }

  function saveWorkingData() {
    if (workingData) {
      const storageKey = `workingData_${pageNumber}`;
      localStorage.setItem(storageKey, JSON.stringify(workingData));
      // Persist via handler for document-level storage as well
      saveWorkingDocument('doc1', workingData);
    }
  }
  
  // Column popup functions moved to SourceTableEditor
  
  // Function to load source data when table is selected
  function loadSourceData() {
    
    if (!selectedSourceTable || !workingData || !effectiveTableId || isUpdatingCell || !show) {
      return;
    }
    
    // Find the corresponding table in working data
    // Use flexible matching to handle slight differences in table names (e.g., .* suffix)
    const workingTable = workingData.tables.find((t: any) => {
      const pageMatch = t.page === selectedSourceTable.page;
      const tName = t.name || t.table_name;
      const sName = selectedSourceTable.name;
      const nameMatch = tName === sName || 
                       tName === sName + '.*' ||
                       sName === tName + '.*';
      return pageMatch && nameMatch;
    });
    
    if (workingTable) {
      currentTableIndex = workingData.tables.indexOf(workingTable);
      
      let currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      
      if (!currentData) {
        // Log available targets for debugging
        if (workingTable && workingTable.work && Array.isArray(workingTable.work)) {
          const availableTargets = workingTable.work.map((t: any) => t.target);
        }
        
        // Create new entry - use preprocessed data from selectedSourceTable if available
        let sourceData = null;
        
        // Check if selectedSourceTable has preprocessed data (status === 'valid' and has data.columns)
        if (selectedSourceTable && selectedSourceTable.status === 'valid' && selectedSourceTable.data && selectedSourceTable.data.columns) {
          sourceData = selectedSourceTable.data;
        } else if (workingTable.data && workingTable.data.columns) {
          // Use workingTable.data if it has columns (preprocessed)
          sourceData = workingTable.data;
        } else if (workingTable.data) {
          // Fallback to workingTable.data even if not preprocessed
          sourceData = workingTable.data;
        }
        
        if (sourceData && workingTable && workingTable.work && Array.isArray(workingTable.work)) {
          // Deep copy data
          const newCurrentEntry = JSON.parse(JSON.stringify(sourceData));
          // Set target property
          newCurrentEntry.target = effectiveTableId;
          
          // Clean up column labels: remove periods completely
          if (newCurrentEntry.columns && Array.isArray(newCurrentEntry.columns)) {
            newCurrentEntry.columns.forEach((col: any) => {
              if (col.label) {
                col.label = col.label.replace(/\./g, '');
              }
            });
          }
          
          // Update workingTable.data to match preprocessed version if we used preprocessed data
          if (sourceData === selectedSourceTable.data && selectedSourceTable.status === 'valid') {
            workingTable.data = JSON.parse(JSON.stringify(selectedSourceTable.data));
            workingTable.status = 'valid';
          }
          
          // Remove any existing entries with same target (clean up)
          const existingIndex = workingTable.work.findIndex((entry: any) => entry.target === effectiveTableId);
          if (existingIndex >= 0) {
            workingTable.work.splice(existingIndex, 1);
          }
          
          // Add new entry to work array
          workingTable.work.push(newCurrentEntry);
          currentData = newCurrentEntry;
          
          // Save the updated working data
          saveWorkingData();
        }
      }
      
      if (currentData) {
        sourceData2D = workingTableTo2DArray(currentData);
        originalSourceData = JSON.parse(JSON.stringify(sourceData2D));
        hasChanges = false;
        
        // Source grid will be initialized by SourceTableEditor component
        // We just set up the data here
      }
    }
  }
  
  // Load source data when table selected (only for source grid, not target)
  // Reset and reload source grid when selectedSourceTable or effectiveTableId changes
  $: if (selectedSourceTable && workingData && !isUpdatingCell && show && effectiveTableId) {
    // Check if the selected table or effectiveTableId actually changed
    const tableChanged = lastSelectedSourceTable !== selectedSourceTable;
    const targetChanged = lastEffectiveTableId !== effectiveTableId;
    const somethingChanged = tableChanged || targetChanged;
    
    // Initialize on first load
    const isFirstLoad = !lastSelectedSourceTable && !lastEffectiveTableId;
    
    if (somethingChanged || isFirstLoad) {
      if (sourceGridInitialized) {
        sourceGridInitialized = false;
      }
      
      lastSelectedSourceTable = selectedSourceTable;
      lastEffectiveTableId = effectiveTableId;
      
      // Now load the data
      loadSourceData();
    }
  }
  
  // Source grid initialization is now handled by SourceTableEditor component
  // Run validation when source grid is ready
  $: if (show && sourceData2D.length > 0 && sourceGridInitialized) {
    runValidation();
  }
  
  // Load target data for source-document and source-study modes
  // DISABLED: This reactive statement was causing conflicts with manual updates
  // NOTE: Now using separate reactive statements for each mode with mode-specific flags (isUpdatingDocumentTarget and isUpdatingStudyTarget)
  // $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && topicId && tableId && currentMode === 'source-document' && !isUpdatingDocumentTarget) {
  //   
  //   const context: DataLoadingContext = {
  //     documentExtractStore: $documentExtractStore,
  //     documentExtractFullStore: $documentExtractFullStore,
  //     studyExtractStore: $studyExtractStore,
  //     projectStructureStore: $projectStructureStore,
  //     topicId,
  //     tableId
  //   };
  //   
  //   targetData2D = loadDataForModeExternal(currentMode, context) as any[][];
  //   
  //   // Initialize target grid
  //   if (show && targetGridDiv) {
  //     setTimeout(() => initializeTargetGrid(), 100);
  //   }
  // }
  
  // Load document and study data for document-study mode
  $: if (show && $documentExtractStore && $documentExtractFullStore && $studyExtractStore && $projectStructureStore && 
      ((isReferenceMode && referenceId) || (isTopicMode && topicId && effectiveTableId)) && 
      currentMode === 'document-study' && (!documentTargetGridInitialized || !studyTargetGridInitialized) && !isUpdatingDocumentTarget && !isUpdatingStudyTarget) {
    
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId: isReferenceMode ? '' : topicId,
      tableId: isReferenceMode ? '' : effectiveTableId,
      referenceId: isReferenceMode ? referenceId : undefined
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { documentData, studyData } = result as { documentData: any[][], studyData: any[][] };
    
    documentTargetData2D = documentData;
    studyTargetData2D = studyData;
    
    // Grid initialization will be handled by reactive statements
  }
  
  // Run validation when both source and target data are available
  // DISABLED: This was causing grid refreshes during cell editing
  
  function runValidation() {
    // Determine which target data to use based on current mode
    let targetData: any[][] = [];
    if (currentMode === 'source-document') {
      targetData = documentTargetData2D;
    } else if (currentMode === 'source-study') {
      targetData = studyTargetData2D;
    }
    
    if (sourceData2D.length > 0 && targetData.length > 0) {
      // Find the key column for validation
      // First, try to find which source column maps to the key column in the target
      let keyColumnLabels: string[] = ['Group']; // Default fallback
      
      if (workingData && currentTableIndex >= 0 && effectiveTableId && $projectStructureStore) {
        // Build key mappings to find the key column
        const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
        const targetHeaders = targetData[0];
        const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
        
        // Find the key column in target (usually the first column or one marked as key)
        // For now, use the first target column as the key column
        let targetKeyColumnLabel = targetHeaders[0];
        let targetKeyColumnKey = targetKeyMapping.get(targetHeaders[0]);
        
        // Try to find which source column maps to this key
        const sourceKeyColumnLabel = Array.from(sourceKeyMapping.entries()).find(([label, key]) => key === targetKeyColumnKey)?.[0];
        
        if (sourceKeyColumnLabel) {
          keyColumnLabels = [sourceKeyColumnLabel];
        } else {
          // Fallback: use 'Group' or first column if available
          if (targetHeaders.length > 0) {
            keyColumnLabels = [targetHeaders[0]];
          }
        }
      }
      
      validationResult = validateSourceAgainstTarget(sourceData2D, targetData, keyColumnLabels);
      syncSummary = getSyncSummary(sourceData2D, targetData, keyColumnLabels);
      
      // Update row match status in workingData
      if (validationResult && validationResult.rowValidation && workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).rows) {
          validationResult.rowValidation.forEach((isMatched: boolean, rowIndex: number) => {
            // rowIndex from validation is 1-based (row 0 is headers)
            const actualRowIndex = rowIndex - 1;
            if (actualRowIndex >= 0 && actualRowIndex < (currentData as any).rows.length) {
              (currentData as any).rows[actualRowIndex].match = isMatched;
            }
          });
          saveWorkingData();
          // Update sourceData2D to reflect match status changes
          // This will trigger SourceTableEditor's reactive statement to refresh the grid
          const updatedCurrentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
          if (updatedCurrentData && workingData.tables[currentTableIndex]) {
            sourceData2D = workingTableTo2DArray(updatedCurrentData);
          }
        }
      }
    }
  }
  
  // Callback to receive sourceGridApi from SourceTableEditor
  function handleSourceGridReady(api: GridApi) {
    sourceGridApi = api;
  }
  
  // Callback to handle selection changes from SourceTableEditor
  function handleSourceSelectionChanged(newSelectedRows: Set<number>) {
    selectedRows = newSelectedRows;
  }
  
  // This function is no longer used - grids are now handled by TopicGrid component
  // Kept for reference but should not be called
  function initializeTargetGrid() {
    logWarning('initializeTargetGrid called but grids are now handled by TopicGrid component', "TopicTableEditor", undefined);
    return;
  }
  
  function convertToGridFormat(data2D: any[][], includeCheckboxes = false, isTargetGrid = false): any {
    if (data2D.length === 0) return { columnDefs: [], rowData: [] };
    
    // Detect if this is a study target grid (not document target grid)
    // We check if data2D is the same reference as studyTargetData2D
    // This ensures indicators are only shown for study target grids, not document target grids
    const isStudyTargetGrid = isTargetGrid && data2D === studyTargetData2D;
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    const columnDefs: any[] = [];
    
    // Get the current working data to access column properties (only for source grids)
    const currentData = !isTargetGrid ? getCurrentVersion(workingData, currentTableIndex, effectiveTableId) : null;
    
    // Add match icon column for source grids (positioned right after checkbox column)
    // Use unshift() to add it early, and AG Grid will place it after the auto-created checkbox column
    if (!isTargetGrid && includeCheckboxes) {
      columnDefs.unshift({
        field: '_match',
        headerName: '',
        width: 28, // Narrow width for icon column
        minWidth: 28, // Ensure it stays narrow
        maxWidth: 28, // Prevent expansion
        cellClass: 'match-icon-cell', // Custom class to override padding
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        resizable: false,
        sortable: false,
        filter: false,
        editable: false,
        lockPosition: true,
        suppressMovable: true,
        cellRenderer: (params: any) => {
          let sp = '<span style="display: inline-flex; align-items: center; justify-content: center; width: 100%; height: 100%;"';
          if (params.value === true || params.value === 'true') {
            sp = sp + 'title="Row is mapped"><svg class="w-5 h-5 match-icon" viewBox="0 0 640 640"><path fill="currentColor" d="M320 576C178.6 576 64 461.4 64 320C64 178.6 178.6 64 320 64C461.4 64 576 178.6 576 320C576 461.4 461.4 576 320 576zM438 209.7C427.3 201.9 412.3 204.3 404.5 215L285.1 379.2L233 327.1C223.6 317.7 208.4 317.7 199.1 327.1C189.8 336.5 189.7 351.7 199.1 361L271.1 433C276.1 438 282.9 440.5 289.9 440C296.9 439.5 303.3 435.9 307.4 430.2L443.3 243.2C451.1 232.5 448.7 217.5 438 209.7z"/></svg></span>';
            return sp;  
          } else if (params.value === false || params.value === 'false') {
            sp = sp +  'title="Row is not mapped"><svg class="w-5 h-5 no-match-icon" viewBox="0 0 20 20"><path fill="currentColor" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path></svg></span>';
            return sp;
          }
          return sp + '></span>';
        }
      });
    }
    
    // Add data columns with custom header components
    headers.forEach((header: string, index: number) => {
      let columnKey = '-';
      let isMapped = false;
      
      if (!isTargetGrid) { // Logic for source grid
      const workingColumn = (currentData as any)?.columns?.find((col: any) => col.label === header);
        columnKey = workingColumn?.key || '-';
        isMapped = workingColumn?.match || false;
      } else { // Logic for target grid
        // We'll find columnStructure here and use it for both columnKey and editor config
      }
      
      // For target grids, find column structure for editor config
      let displayLabel = header;
      let displayKey = columnKey;
      let isMultiline = false;
      let columnStructure: any = null;
      
      if (isTargetGrid && isReferenceMode && $projectStructureStore && referenceId) {
        const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
        if (referenceStructure) {
          const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
          // Try multiple matching strategies: by label, by ID, or by columnKey
          columnStructure = columns.find((col: any) => 
            col.label === header || 
            col.id === header || 
            col.id === columnKey ||
            col.label === columnKey
          );
          if (columnStructure) {
            displayLabel = columnStructure.label || columnStructure.id || header;
            displayKey = columnStructure.id || columnKey;
            columnKey = columnStructure.id; // Set columnKey for consistency
            isMultiline = columnStructure.multiline === true;
          }
        }
      } else if (isTargetGrid && !isReferenceMode && $projectStructureStore && topicId && effectiveTableId) {
        // Topic mode: get column structure for editor config
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        if (topicStructure) {
          const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
          if (tableStructure && tableStructure.columns) {
            // Try multiple matching strategies: by label, by ID, or by columnKey
            columnStructure = tableStructure.columns.find((col: any) => 
              col.label === header || 
              col.id === header ||
              col.id === columnKey ||
              col.label === columnKey
            );
            
            if (columnStructure) {
              displayLabel = columnStructure.label || columnStructure.id || header;
              displayKey = columnStructure.id || columnKey;
              
              // Set columnKey based on column type
              if (columnStructure.refTable === 'grouper') {
                // For grouper references, use the grouper LEVEL ID (e.g., "group")
                const grouperStructure = topicStructure.grouper;
                if (grouperStructure && grouperStructure.levels && grouperStructure.levels.length > 0) {
                  columnKey = grouperStructure.levels[0].id; // e.g., "group"
                }
              } else {
                // For regular columns, use the column ID directly
                columnKey = columnStructure.id;
              }
              
              isMultiline = columnStructure.multiline === true;
            } else if (index < 3) {
              logWarning(`No column structure found for header "${header}"`, "TopicTableEditor", undefined);
            }
          }
        }
      }
      
      const isFirstColumn = index === 0;
      const isFixed = isFirstColumn;
      
      // Check if this is a grouper column (for both source and target grids)
      let isGrouperColumn = false;
      if (columnStructure && columnStructure.refTable === 'grouper') {
        isGrouperColumn = true;
      } else if (!isTargetGrid && currentData) {
        // For source grids, check if the mapped key is a grouper level
        const workingColumn = currentData.columns?.find((col: any) => col.label === header);
        if (workingColumn?.key && $projectStructureStore && !isReferenceMode && topicId && effectiveTableId) {
          const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
          if (topicStructure) {
            const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
            if (tableStructure) {
              const targetColumn = tableStructure.columns?.find((col: any) => col.id === workingColumn.key);
              if (targetColumn && targetColumn.refTable === 'grouper') {
                isGrouperColumn = true;
              }
            }
          }
        }
      }
      
      // For source grids, check if we can get column structure from target
      // This helps when source columns are mapped to target columns
      if (!isTargetGrid && currentData) {
        const workingColumn = currentData.columns?.find((col: any) => col.label === header);
        if (workingColumn?.key && $projectStructureStore) {
          // Try to find the target column with this key
          if (!isReferenceMode && topicId && effectiveTableId) {
            const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
            if (topicStructure) {
              const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
              if (tableStructure) {
                const targetColumn = tableStructure.columns?.find((col: any) => col.id === workingColumn.key);
                if (targetColumn) {
                  columnStructure = targetColumn;
                  isMultiline = targetColumn.multiline === true;
                }
              }
            }
          } else if (isReferenceMode && referenceId) {
            const referenceStructure = $projectStructureStore.references?.find((r: any) => r.id === referenceId);
            if (referenceStructure) {
              const columns = referenceStructure.columns || referenceStructure.structure?.columns || [];
              const targetColumn = columns.find((col: any) => col.id === workingColumn.key);
              if (targetColumn) {
                columnStructure = targetColumn;
                isMultiline = targetColumn.multiline === true;
              }
            }
          }
        }
      }
      
      // Use columnStructure if available, otherwise create a minimal column object for editor config
      const columnForEditor = columnStructure || { id: displayKey, label: displayLabel, multiline: isMultiline, refTable: isGrouperColumn ? 'grouper' : undefined };
            
      // Get cell editor configuration using shared utility (skip for grouper columns)
      const editorConfig = isGrouperColumn ? { cellEditor: undefined, cellEditorPopup: undefined, cellEditorParams: undefined } : getCellEditorConfig(columnForEditor, $projectStructureStore);
      
      
      // Get lookup value getter/setter if this is a lookup column (skip for grouper columns)
      const lookupValueConfig = isGrouperColumn ? null : getLookupValueGetterSetter(columnForEditor, $projectStructureStore);
      
      // Get lookup cell renderer if this is a lookup column (skip for grouper columns)
      const lookupRenderer = isGrouperColumn ? null : getLookupCellRenderer(columnForEditor, $projectStructureStore);
      
      columnDefs.push({
        field: header,
        headerName: displayLabel, // Use label for display
        suppressHeaderMenuButton: true,
        suppressHeaderContextMenu: true,
        headerComponent: isTargetGrid ? 'columnHeaderForTarget' : 'columnHeaderForSource',
        headerComponentParams: {
          columnIndex: index,
          columnName: displayLabel,
          displayName: displayLabel, // Explicitly pass displayName for custom header component
          key: displayKey,
          isMapped: isMapped,
          onEditColumn: (colIndex: number, event?: MouseEvent) => {
            // Handled by SourceTableEditor internally
          },
          onDeleteColumn: (colIndex: number) => {
            // Handled by SourceTableEditor internally
          }
        },
        // Multiline support for both source and target grids (like ExtractItems2)
        wrapText: true,
        autoHeight: true,
        editable: !isGrouperColumn, // Grouper columns are read-only
        ...editorConfig,
        // Set widths based on column type
        // Multiline and fixed columns get fixed width
        // Regular columns get minWidth and let autoSizeStrategy handle sizing
        width: isMultiline ? 200 : (isFixed ? 150 : undefined),
        minWidth: isMultiline ? 200 : (isFixed ? 150 : 80), // Minimum width for regular columns
        maxWidth: isMultiline ? 200 : (isFixed ? 150 : undefined), // No max for regular columns to allow auto-sizing
        suppressSizeToFit: isMultiline || isFixed,
        suppressAutoSize: isMultiline || isFixed,
        cellStyle: {
          'white-space': 'normal',
          'word-wrap': 'break-word',
          'overflow-wrap': 'break-word',
          'padding': '8px 8px',
          'line-height': 'normal',
          'vertical-align': 'top'
        },
        // Convert "-" to "" for editing, "" to "-" for display
        // For lookup columns, use the shared utility's valueGetter/valueSetter
        valueGetter: (params: any) => {
          const value = params.data[header];
          if (value === '-' || value === undefined || value === null) return '';
          
          // Use lookup value getter if available
          if (lookupValueConfig?.valueGetter) {
            return lookupValueConfig.valueGetter(params);
          }
          
          return value;
        },
        valueSetter: (params: any) => {
          let value = params.newValue === '' || params.newValue === null || params.newValue === undefined ? '-' : params.newValue;
          
          // Use lookup value setter if available
          if (lookupValueConfig?.valueSetter) {
            return lookupValueConfig.valueSetter(params);
          }
          
          params.data[header] = value;
          return true;
        },
        cellRenderer: (() => {
          // Capture columnId in closure for study target grid indicators
          const columnId = columnForEditor.id;
          const isStudyTargetForThisColumn = isStudyTargetGrid;
          const lookupRendererForColumn = lookupRenderer;
          
          return (params: any) => {
            // For lookup columns, use the lookup renderer
            if (lookupRendererForColumn) {
              // For study target grids, we need to wrap the lookup renderer result with indicators
              if (isStudyTargetForThisColumn) {
                const lookupResult = lookupRendererForColumn(params);
              // If lookup renderer returns a string, we can add indicators
              // Otherwise, return as-is (lookup renderers may return complex HTML)
              if (typeof lookupResult === 'string') {
                const value = params.value || '-';
                const sourceInfo = getSourceInfoForCell(params.node.rowIndex, columnId);
                return createCellWithIndicator(value, sourceInfo, currentDocumentID);
              }
                return lookupResult;
              }
              return lookupRendererForColumn(params);
            }
            
            // For study target grids, add source indicators
            if (isStudyTargetForThisColumn) {
              const value = params.value || '-';
              const sourceInfo = getSourceInfoForCell(params.node.rowIndex, columnId);
              return createCellWithIndicator(value, sourceInfo, currentDocumentID);
            }
            
            // Just show the data for document target grids and source grids
            return params.value || '-';
          };
        })()
      });
    });
    
    const rowData: any[] = [];
    
    // No need for column selection row - checkboxes are now in headers
    
    // Add data rows
    rows.forEach((row: any[], rowIndex: number) => {
      const rowObj: any = {};
      if (includeCheckboxes) {
        rowObj.rowSelection = '';
        
        // Add match value from work data for source grids
        if (!isTargetGrid && currentData && currentData.rows && currentData.rows[rowIndex]) {
          rowObj._match = currentData.rows[rowIndex].match;
        }
      }
      headers.forEach((header: string, index: number) => {
        rowObj[header] = row[index];
      });
      rowData.push(rowObj);
    });
    
    return { columnDefs, rowData };
  }
  
  function updateSourceDataFromGridSilently() {
    if (!sourceGridApi) return;
    
    const headers = sourceData2D[0];
    const newData = [headers];
    
    sourceGridApi.forEachNode((node: any) => {
      if (node.data) {
        const row = headers.map((header: string) => node.data[header] || '');
        newData.push(row);
      }
    });
    
    // Update the data without triggering reactive updates
    sourceData2D = newData;
  }
  
  function updateSourceDataFromGrid() {
    if (!sourceGridApi) return;
    
    const headers = sourceData2D[0];
    const newData = [headers];
    
    sourceGridApi.forEachNode((node: any) => {
      if (node.data) {
        const row = headers.map((header: string) => node.data[header] || '');
        newData.push(row);
      }
    });
    
    sourceData2D = newData;
  }

  function updateWorkingDataFromGrid(params: any) {
    if (!workingData || !params) return;
    
    // Set flag to prevent reactive updates
    isUpdatingCell = true;
    
    // Use params.data[colDef.field] to get the value after valueSetter conversion (handles lookup ID conversion)
    const { rowIndex, colDef } = params;
    const cellValue = params.data[colDef.field]; // This has the ID after valueSetter conversion
    
    const headers = sourceData2D[0];
    const colIndex = headers.indexOf(colDef.field);
    
    if (colIndex >= 0 && rowIndex >= 0) {
      // Find the corresponding row and column in working data
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData && (currentData as any).rows && (currentData as any).columns && (currentData as any).rows[rowIndex] && (currentData as any).columns[colIndex]) {
        const rowId = (currentData as any).rows[rowIndex].id;
        const colId = (currentData as any).columns[colIndex].id;
        
        updateCellValue(workingData, currentTableIndex, rowId, colId, cellValue, "user", effectiveTableId);
        saveWorkingData();
      }
    }
    
    // Clear flag after a brief delay
    setTimeout(() => {
      isUpdatingCell = false;
    }, 100);
  }
  
  function updateTargetGrid() {
    // Grid updates are handled reactively via TopicGrid props.
  }
  
  // This function is no longer used - grids are now handled by TopicGrid component
  function initializeDocumentTargetGrid() {
    logWarning('initializeDocumentTargetGrid called but grids are now handled by TopicGrid component', "TopicTableEditor", undefined);
    return;
  }
  
  // Build row ID mapping from study extract data - reactive
  $: if ($studyExtractStore && topicId && effectiveTableId && studyTargetData2D.length > 0) {
    studyTargetRowIdMap.clear();
    const topicData = $studyExtractStore.topics?.find((t: any) => t.id === topicId);
    const tableData = topicData?.tables?.find((t: any) => t.id === effectiveTableId);
    if (tableData?.rows) {
      tableData.rows.forEach((row: any, index: number) => {
        if (row.id) {
          studyTargetRowIdMap.set(index, row.id);
        }
      });
    }
  }
  
  // Build row ID mapping from document extract data - reactive
  $: if ($documentExtractFullStore && topicId && effectiveTableId && documentTargetData2D.length > 0) {
    documentTargetRowIdMap.clear();
    const topicData = $documentExtractFullStore.topics?.find((t: any) => t.id === topicId);
    const tableData = topicData?.tables?.find((t: any) => t.id === effectiveTableId);
    if (tableData?.rows) {
      tableData.rows.forEach((row: any, index: number) => {
        if (row.id) {
          documentTargetRowIdMap.set(index, row.id);
        }
      });
    }
  }
  
  function updateStudyTargetGrid() {
    // Grid updates are handled reactively via TopicGrid props.
  }
  
  // Handler for document target grid cell value changes
  function handleDocumentTargetCellValueChanged(params: any) {
    const { rowIndex, colDef, newValue, data } = params;
    const cellValue = data[colDef.field];
    
    if (!$documentExtractFullStore || !topicId || !effectiveTableId || !$projectStructureStore) {
      logWarning('Cannot update cell - missing required data', "TopicTableEditor", undefined);
      return;
    }
    
    const rowIdFromData = data?.id || params.node?.data?.id;
    const rowId = rowIdFromData || documentTargetRowIdMap.get(rowIndex);
    if (!rowId) {
      logWarning(`Cannot find row ID for rowIndex ${rowIndex}`, "TopicTableEditor", undefined);
      return;
    }
    
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) {
      logWarning(`Topic not found: ${topicId}`, "TopicTableEditor", undefined);
      return;
    }
    
    const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
    if (!tableStructure) {
      logWarning(`Table not found: ${effectiveTableId}`, "TopicTableEditor", undefined);
      return;
    }
    
    const columnStructure = tableStructure.columns?.find((col: any) => 
      col.label === colDef.headerName || 
      col.label === colDef.field ||
      col.id === colDef.field
    );
    
    if (!columnStructure) {
      logWarning(`Column not found for field: ${colDef.field}`, "TopicTableEditor", undefined);
      return;
    }
    
    if (columnStructure.refTable === 'grouper') {
      logWarning('Attempted to edit grouper column - this should not happen', "TopicTableEditor", undefined);
      return;
    }
    
    const columnId = columnStructure.id;
    const currentUser = $userStore?.user;
    const userId = currentUser?.user_id || currentUser?.oid || 'unknown';
    const userName = currentUser?.name || currentUser?.email || 'Unknown User';
    
    // Update document extract store
    const updatedDocument = updateTargetCell(
      $documentExtractFullStore,
      topicId,
      effectiveTableId,
      rowId,
      columnId,
      cellValue,
      userId,
      userName
    );
    
    documentExtractFullStore.set(updatedDocument);
    
    // Update the 2D array
    const headers = documentTargetData2D[0];
    const colIndex = headers.indexOf(colDef.field);
    if (colIndex >= 0 && rowIndex >= 0) {
      const actualRowIndex = rowIndex + 1;
      if (actualRowIndex < documentTargetData2D.length) {
        documentTargetData2D[actualRowIndex][colIndex] = cellValue;
      }
    }
  }
  
  // Handler for study target grid cell value changes
  function handleStudyTargetCellValueChanged(params: any) {
    const { rowIndex, colDef, newValue, data } = params;
    const cellValue = data[colDef.field];
    
    if (!$studyExtractStore || !topicId || !effectiveTableId || !$projectStructureStore) {
      logWarning('Cannot update cell - missing required data', "TopicTableEditor", undefined);
      return;
    }
    
    const rowIdFromData = data?.id || params.node?.data?.id;
    const rowId = rowIdFromData || studyTargetRowIdMap.get(rowIndex);
    if (!rowId) {
      logWarning(`Cannot find row ID for rowIndex ${rowIndex}`, "TopicTableEditor", undefined);
      return;
    }
    
    const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
    if (!topicStructure) {
      logWarning(`Topic not found: ${topicId}`, "TopicTableEditor", undefined);
      return;
    }
    
    const tableStructure = topicStructure.tables?.find((t: any) => t.id === effectiveTableId);
    if (!tableStructure) {
      logWarning(`Table not found: ${effectiveTableId}`, "TopicTableEditor", undefined);
      return;
    }
    
    const columnStructure = tableStructure.columns?.find((col: any) => 
      col.label === colDef.headerName || 
      col.label === colDef.field ||
      col.id === colDef.field
    );
    
    if (!columnStructure) {
      logWarning(`Column not found for field: ${colDef.field}`, "TopicTableEditor", undefined);
      return;
    }
    
    if (columnStructure.refTable === 'grouper') {
      logWarning('Attempted to edit grouper column - this should not happen', "TopicTableEditor", undefined);
      return;
    }
    
    const columnId = columnStructure.id;
    const currentUser = $userStore?.user;
    const userId = currentUser?.user_id || currentUser?.oid || 'unknown';
    const userName = currentUser?.name || currentUser?.email || 'Unknown User';
    
    const updatedStudy = updateTargetCell(
      $studyExtractStore,
      topicId,
      effectiveTableId,
      rowId,
      columnId,
      cellValue,
      userId,
      userName
    );
    
    studyExtractStore.set(updatedStudy);
    
    const headers = studyTargetData2D[0];
    const colIndex = headers.indexOf(colDef.field);
    if (colIndex >= 0 && rowIndex >= 0) {
      const actualRowIndex = rowIndex + 1;
      if (actualRowIndex < studyTargetData2D.length) {
        studyTargetData2D[actualRowIndex][colIndex] = cellValue;
      }
    }
  }
  
  function handleSaveAndClose() {
    if (!$studyExtractStore) return;
    
    try {
      // Cells are already updated directly via onCellValueChanged, so just save to localStorage
      // Make a deep copy to ensure we're saving the latest state
      const study = JSON.parse(JSON.stringify($studyExtractStore));
      
      // Save to localStorage
      saveTargetExtract('study', 'study_1', study);
      
      
      show = false;
    } catch (error) {
      logError(error, "TopicTableEditor", undefined, { message: "Error saving data" });
      alert('Error saving data. Please check console for details.');
    }
  }
  
  function handleCancel() {
    show = false;
    sourceGridInitialized = false; // Reset source grid initialization flag
    documentTargetGridInitialized = false; // Reset document target grid initialization flag
    studyTargetGridInitialized = false; // Reset study target grid initialization flag
  }

  // Column popup functions moved to SourceTableEditor
  // Callback handlers for SourceTableEditor
  
  function handleSaveColumn(columnName: string, columnKey: string, mode: 'add' | 'edit', columnIndex: number) {
    if (mode === 'add') {
      // Add new column using next_col
      if (workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).next_col) {
          // Use the next_col value as the new column ID
          const newColumnId = (currentData as any).next_col;
          
          // Increment next_col for future additions
          const nextColNumber = parseInt((currentData as any).next_col.replace('c', '')) + 1;
          (currentData as any).next_col = `c${nextColNumber}`;
          
          // Add the new column
          addColumn(workingData, currentTableIndex, columnName, columnKey, effectiveTableId);
          
          // Set match status for the newly added column
          const newColumnIndex = (currentData as any).columns.length - 1;
          if ((currentData as any).columns[newColumnIndex]) {
            (currentData as any).columns[newColumnIndex].match = !!columnKey && columnKey.trim() !== '';
          }
          
          saveWorkingData();
          handleRefreshSourceData();
        }
      }
    } else if (mode === 'edit' && columnIndex >= 0) {
      // Edit existing column
      if (workingData && currentTableIndex >= 0) {
        const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
        if (currentData && (currentData as any).columns && (currentData as any).columns[columnIndex]) {
          // Update the column name in sourceData2D
          sourceData2D[0][columnIndex] = columnName;
          
          // Update working data
          const column = (currentData as any).columns[columnIndex];
          column.label = columnName;
          column.key = columnKey;
          // Set match based on whether key is assigned
          column.match = !!columnKey && columnKey.trim() !== '';
          saveWorkingData();
          handleRefreshSourceData();
          // Run validation after column key change to update row validation
          runValidation();
        }
      }
    }
  }
  
  function handleRemoveColumn(columnIndex: number) {
    if (workingData && currentTableIndex >= 0) {
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData) {
        const colId = (currentData as any).columns && (currentData as any).columns[columnIndex] ? (currentData as any).columns[columnIndex].id : null;
        if (colId) {
          removeColumn(workingData, currentTableIndex, colId, effectiveTableId);
          saveWorkingData();
          handleRefreshSourceData();
          selectedColumns.clear();
        }
      }
    }
  }

  function updateDocumentExtractStore(data2D: any[][]) {
    try {
      // Create a deep copy of the store
      const updatedStore = JSON.parse(JSON.stringify($documentExtractFullStore));
      
      if (isReferenceMode) {
        // Reference mode
        if (!$projectStructureStore || !referenceId) return;
        
        // Convert 2D array back to reference format
        const referenceData = arrayToReference(data2D, referenceId, $projectStructureStore, updatedStore);
        
        // Find and update the reference
        const reference = updatedStore.references?.find((r: any) => r.id === referenceId);
        if (reference) {
          reference.rows = referenceData.rows;
        } else {
          // Create new reference if it doesn't exist
          if (!updatedStore.references) {
            updatedStore.references = [];
          }
          updatedStore.references.push({
            id: referenceId,
            rows: referenceData.rows
          });
        }
      } else {
        // Topic mode (existing logic)
        if (!$projectStructureStore || !topicId || !effectiveTableId) return;
        
        // Find the topic
        const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
        if (!topic) return;
        
        // Get table structure for column IDs
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        
        // Find the table (grouper editing is handled by separate GrouperEditor component)
        const table = topic.tables?.find((t: any) => t.id === effectiveTableId);
        if (!table) return;
        
        // Get table structure for column IDs
        const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
        if (!tableStructure) return;
        
        // Convert 2D array back to TARGET table format (preserves row IDs)
        const tableData = convert2DArrayToTargetTable(data2D, tableStructure, table);
        table.rows = tableData.rows;
      }
      
      // Update the store
      documentExtractFullStore.set(updatedStore);
      
    } catch (error) {
      logError(error, "TopicTableEditor", undefined, { message: "Error updating documentExtractFullStore" });
    }
  }
  
  function updateStudyExtractStore(data2D: any[][]) {
    try {
      // Create a deep copy of the store
      const updatedStore = JSON.parse(JSON.stringify($studyExtractStore));
      
      if (isReferenceMode) {
        // Reference mode
        if (!$projectStructureStore || !referenceId) return;
        
        // Convert 2D array back to reference format
        const referenceData = arrayToReference(data2D, referenceId, $projectStructureStore, updatedStore);
        
        // Find and update the reference
        const reference = updatedStore.references?.find((r: any) => r.id === referenceId);
        if (reference) {
          reference.rows = referenceData.rows;
        } else {
          // Create new reference if it doesn't exist
          if (!updatedStore.references) {
            updatedStore.references = [];
          }
          updatedStore.references.push({
            id: referenceId,
            rows: referenceData.rows
          });
        }
      } else {
        // Topic mode (existing logic)
        if (!$projectStructureStore || !topicId || !effectiveTableId) return;
        
        // Find the topic
        const topic = updatedStore.topics?.find((t: any) => t.id === topicId);
        if (!topic) return;
        
        // Find the table
        const table = topic.tables?.find((t: any) => t.id === effectiveTableId);
        if (!table) return;
        
        // Get table structure for column IDs
        const topicStructure = $projectStructureStore.topics?.find((t: any) => t.id === topicId);
        const tableStructure = topicStructure?.tables?.find((t: any) => t.id === effectiveTableId);
        if (!tableStructure) return;
        
        // Convert 2D array back to TARGET table format (preserves row IDs)
        const tableData = convert2DArrayToTargetTable(data2D, tableStructure, table);
        table.rows = tableData.rows;
      }
      
      // Update the store
      studyExtractStore.set(updatedStore);
    } catch (error) {
      logError(error, "TopicTableEditor", undefined, { message: "Error updating studyExtractStore" });
    }
  }
  
  /**
   * Convert 2D array to TARGET table format (document/study extract)
   * Preserves original row IDs like "row_study", "row_arm_1", etc.
   * Generates "r_new1", "r_new2" for new rows
   */
  function convert2DArrayToTargetTable(data2D: any[][], tableStructure: any, originalTableData?: any): any {
    if (data2D.length < 2) return { rows: [] };
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    // Find the highest existing r_new{N} number
    let maxNewRowNum = 0;
    if (originalTableData?.rows) {
      originalTableData.rows.forEach((row: any) => {
        if (row.id.startsWith('r_new')) {
          const num = parseInt(row.id.substring(5));
          if (!isNaN(num) && num > maxNewRowNum) {
            maxNewRowNum = num;
          }
        }
      });
    }
    let newRowCounter = maxNewRowNum;
    
    return {
      rows: rows.map((row, index) => {
        const cells: any[] = [];
        
        headers.forEach((header, colIndex) => {
          // Find the column in the table structure that matches this header label
          const column = tableStructure.columns?.find((col: any) => col.label === header);
          const columnId = column?.id || header.toLowerCase().replace(/\s+/g, '_').replace(/[?!]/g, '');
          
          // Create cell object matching the expected structure (cells array with id and value)
          cells.push({
            id: columnId,
            value: row[colIndex] || ''
          });
        });
        
        // Preserve existing row ID if available, otherwise generate new ID
        const existingRow = originalTableData?.rows?.[index];
        let rowId;
        if (existingRow?.id) {
          rowId = existingRow.id;
        } else {
          // New row - generate r_new{N}
          newRowCounter++;
          rowId = `r_new${newRowCounter}`;
        }
        
        return {
          id: rowId,
          cells: cells
        };
      })
    };
  }
  
  /**
   * Convert 2D array to SOURCE table format (working data)
   * Always generates sequential IDs: r1, r2, r3, etc.
   */
  function convert2DArrayToSourceTable(data2D: any[][]): any {
    if (data2D.length < 2) return { rows: [] };
    
    const headers = data2D[0];
    const rows = data2D.slice(1);
    
    return {
      rows: rows.map((row, index) => {
        const rowObj: any = { id: `r${index + 1}` };
        headers.forEach((header, colIndex) => {
          const key = header.toLowerCase().replace(/\s+/g, '_').replace(/[?!]/g, '');
          rowObj[key] = row[colIndex] || '';
        });
        return rowObj;
      })
    };
  }
  
  // Grouper editing functions removed - use GrouperEditor component instead
  // Functions removed:
  // - convertGrouperToEditorGridFormat
  // - getSourceOptions (for grouper)
  // - findRefIDByDisplayName (for grouper)
  // - handleGrouperCellChange

  function handleTargetAddRow() {
    
    // Determine which grid and data to use based on current mode
    const isDocumentMode = currentMode === 'source-document';
    
    // Grouper editing is handled by separate GrouperEditor component
    
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    
    // Set the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = true;
    } else {
      isUpdatingStudyTarget = true;
    }
    
    if (targetData.length > 0) {
      // Use consolidated function from target-table-processing
      const updatedData = addTargetRow(targetData);
      
      // Update the appropriate targetData and store
      if (isDocumentMode) {
        documentTargetData2D = updatedData;
        updateDocumentExtractStore(documentTargetData2D);
        // Save to localStorage
        if ($documentExtractFullStore) {
          saveTargetExtract('document', 'document_1', $documentExtractFullStore);
        }
      } else {
        studyTargetData2D = updatedData;
        updateStudyExtractStore(studyTargetData2D);
        // Save to localStorage
        if ($studyExtractStore) {
          saveTargetExtract('study', 'study_1', $studyExtractStore);
        }
      }
      
    }
    
    // Clear the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = false;
      documentTargetGridInitialized = true;
    } else {
      isUpdatingStudyTarget = false;
      studyTargetGridInitialized = true;
    }
  }

  function handleTargetRemoveRow() {
    // Determine which grid and data to use based on current mode
    const isDocumentMode = currentMode === 'source-document';
    const selectedRows = isDocumentMode ? selectedDocumentTargetRows : selectedStudyTargetRows;
    const targetData = isDocumentMode ? documentTargetData2D : studyTargetData2D;
    
    
    // Set the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = true;
    } else {
      isUpdatingStudyTarget = true;
    }
    
    if (selectedRows.size > 0) {
      // Use consolidated function from target-table-processing
      const rowIndices = Array.from(selectedRows);
      const updatedData = removeTargetRows(targetData, rowIndices);
      
      // Update the appropriate targetData and store
      if (isDocumentMode) {
        documentTargetData2D = updatedData;
        updateDocumentExtractStore(documentTargetData2D);
        // Save to localStorage
        if ($documentExtractFullStore) {
          saveTargetExtract('document', 'document_1', $documentExtractFullStore);
        }
      } else {
        studyTargetData2D = updatedData;
        updateStudyExtractStore(studyTargetData2D);
        // Save to localStorage
        if ($studyExtractStore) {
          saveTargetExtract('study', 'study_1', $studyExtractStore);
        }
      }
      
      // Clear selection
      if (isDocumentMode) {
        selectedDocumentTargetRows.clear();
      } else {
        selectedStudyTargetRows.clear();
      }
      
    }
    
    // Clear the appropriate flag based on current mode
    if (isDocumentMode) {
      isUpdatingDocumentTarget = false;
      documentTargetGridInitialized = true;
    } else {
      isUpdatingStudyTarget = false;
      studyTargetGridInitialized = true;
    }
  }

  function handleDocumentTargetTableReset() {
    // Reload document target data
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { documentData } = result as { documentData: any[][], studyData: any[][] };
    documentTargetData2D = documentData;
  }

  function handleStudyTargetTableReset() {
    // Reload study target data
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    const result = loadDataForModeExternal('document-study', context);
    const { studyData } = result as { documentData: any[][], studyData: any[][] };
    studyTargetData2D = studyData;
  }

  function handleTargetTableReset() {
    // Reset target table to original state
    // This would need to reload the original target data from the appropriate source
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId,
      tableId
    };
    
    if (currentMode === 'source-document') {
      // Reload from document extract
      documentTargetData2D = loadDataForModeExternal(currentMode, context) as any[][];
      updateTargetGrid();
    } else if (currentMode === 'source-study') {
      // Reload from study extract
      studyTargetData2D = loadDataForModeExternal(currentMode, context) as any[][];
      updateTargetGrid();
    }
  }

  // Source grid operations moved to SourceTableEditor
  // Callback handlers for SourceTableEditor
  
  // No-op functions for SourceTableEditor callbacks (handled internally)
  const noop = () => {};
  const noopWithEvent = (e?: MouseEvent) => {};
  
  function handleRefreshSourceData() {
    // Refresh sourceData2D from workingData
    if (workingData && currentTableIndex >= 0) {
      const workingTable = workingData.tables[currentTableIndex];
      const currentData = getCurrentVersion(workingData, currentTableIndex, effectiveTableId);
      if (currentData && workingTable) {
        sourceData2D = workingTableTo2DArray(currentData);
        originalSourceData = JSON.parse(JSON.stringify(sourceData2D));
        hasChanges = false;
        
        // SourceTableEditor will automatically refresh the grid via its reactive statement
        // when sourceData2D changes
        
        runValidation();
      }
    }
  }

  function handleMatchOnly() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    if (!validationResult || validationResult.grouperColumns.length === 0) {
      alert('No grouper columns found. Cannot perform match-only sync.');
      return;
    }
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    if (currentMode === 'source-document') {
      documentTargetData2D = matchOnlySyncWithKeys(sourceData2D, documentTargetData2D, validationResult.grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchOnlySyncWithKeys(sourceData2D, studyTargetData2D, validationResult.grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  
  function handleMatchAll() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, sourceData2D[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    if (currentMode === 'source-document') {
      documentTargetData2D = matchAllSyncWithKeys(sourceData2D, documentTargetData2D, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchAllSyncWithKeys(sourceData2D, studyTargetData2D, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  

  function handleDetectMappings() {
    if (validationResult) {
      const summary = getValidationSummary(validationResult);
      alert(`Validation Results:\n\n${summary}\n\nSync Preview:\n${syncSummary}`);
    }
  }
  
  // Grid initialization is handled in the individual reactive statements above
  
  // Mode change handler
  function handleModeChange(mode: FlowMode) {
    currentMode = mode;
    
    // Clear selection state when switching modes
    selectedRows.clear();
    selectedColumns.clear();
    selectedDocumentTargetRows.clear();
    selectedStudyTargetRows.clear();
    
    // Reset flags and grids when mode changes
    sourceGridInitialized = false; // Reset source grid initialization flag
    documentTargetGridInitialized = false; // Reset document target grid initialization flag
    studyTargetGridInitialized = false; // Reset study target grid initialization flag
    resetGrids();
    loadDataForMode();
  }
  
  function resetGrids() {
    // Destroy all existing grids (source grid is handled by SourceTableEditor)
    sourceGridApi = null; // SourceTableEditor will handle its own grid destruction
    if (documentTargetGridInstance) {
      documentTargetGridInstance.destroy();
      documentTargetGridInstance = null;
      documentTargetGridApi = null;
    }
    if (studyTargetGridInstance) {
      studyTargetGridInstance.destroy();
      studyTargetGridInstance = null;
      studyTargetGridApi = null;
    }
    
    sourceGridInitialized = false; // SourceTableEditor manages this
    documentTargetGridInitialized = false;
    studyTargetGridInitialized = false;
  }
  
  function loadDataForMode() {
    const context: DataLoadingContext = {
      documentExtractStore: $documentExtractStore,
      documentExtractFullStore: $documentExtractFullStore,
      studyExtractStore: $studyExtractStore,
      projectStructureStore: $projectStructureStore,
      topicId: isReferenceMode ? '' : topicId,
      tableId: isReferenceMode ? '' : effectiveTableId,
      referenceId: isReferenceMode ? referenceId : undefined
    };
    
    const result = loadDataForModeExternal(currentMode, context);
    
    switch (currentMode) {
      case 'source-document':
        documentTargetData2D = result as any[][];
        // Grid initialization will be handled by reactive statement
        break;
      case 'source-study':
        studyTargetData2D = result as any[][];
        // Grid initialization will be handled by reactive statement
        break;
      case 'document-study':
        const { documentData, studyData } = result as { documentData: any[][], studyData: any[][] };
        documentTargetData2D = documentData;
        studyTargetData2D = studyData;
        // Grid initialization will be handled by reactive statements
        break;
    }
  }
  
  // New handler functions for the additional buttons
  function handleMatchSelected() {
    if (!hasColumnMatch) {
      alert('No columns match between source and target. Cannot perform replace operation.');
      return;
    }
    
    const selectedRows = getSelectedRowsFromGrid(sourceGridApi);
    
    if (selectedRows.size === 0) {
      alert('Please select rows to replace');
      return;
    }
    
    // Filter source data to only include selected rows
    const filteredSourceData = filterRowsBySelection(sourceData2D, selectedRows);
    
    if (filteredSourceData.length <= 1) {
      alert('No valid source rows selected');
      return;
    }
    
    // Build column key mappings
    const sourceKeyMapping = buildSourceKeyMapping(workingData, currentTableIndex, effectiveTableId, filteredSourceData[0]);
    const targetHeaders = currentMode === 'source-document' ? documentTargetData2D[0] : studyTargetData2D[0];
    const targetKeyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, targetHeaders, referenceId);
    
    // Determine which target data to update based on current mode
    // Use matchSelectedSync which both updates matching rows AND adds new rows
    if (currentMode === 'source-document') {
      documentTargetData2D = matchSelectedSyncWithKeys(filteredSourceData, documentTargetData2D, validationResult.grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateDocumentExtractStore(documentTargetData2D);
    } else if (currentMode === 'source-study') {
      studyTargetData2D = matchSelectedSyncWithKeys(filteredSourceData, studyTargetData2D, validationResult.grouperColumns, sourceKeyMapping, targetKeyMapping);
      updateStudyExtractStore(studyTargetData2D);
    }
    updateTargetGrid();
  }
  
  function handleDocumentTargetAddRow() {
    
    // Grouper editing is handled by separate GrouperEditor component
    
    isUpdatingDocumentTarget = true; // Prevent reactive statement from interfering
    
    if (documentTargetData2D.length > 0) {
      // Use consolidated function from target-table-processing
      const updatedData = addTargetRow(documentTargetData2D);
      documentTargetData2D = updatedData;
      
      // Update the document extract store
      updateDocumentExtractStore(documentTargetData2D);
      
      // Save to localStorage
      if ($documentExtractFullStore) {
        saveTargetExtract('document', 'document_1', $documentExtractFullStore);
      }
      
    }
    
    isUpdatingDocumentTarget = false; // Re-enable reactive statements
    documentTargetGridInitialized = true; // Mark document target grid as initialized to prevent reactive statement from running
  }
  
  function handleStudyTargetAddRow() {
    isUpdatingStudyTarget = true; // Prevent reactive statement from interfering
    
    if (studyTargetData2D.length > 0) {
      // Use consolidated function from target-table-processing
      const updatedData = addTargetRow(studyTargetData2D);
      studyTargetData2D = updatedData;
      
      // Update the study extract store
      updateStudyExtractStore(studyTargetData2D);
      
      // Save to localStorage
      if ($studyExtractStore) {
        saveTargetExtract('study', 'study_1', $studyExtractStore);
      }
      
    }
    
    isUpdatingStudyTarget = false; // Re-enable reactive statements
    studyTargetGridInitialized = true; // Mark study target grid as initialized to prevent reactive statement from running
  }

  function handleDocumentTargetRemoveRow() {
    isUpdatingDocumentTarget = true; // Prevent reactive statement from interfering
    
    if (selectedDocumentTargetRows.size > 0) {
      // Use consolidated function from target-table-processing
      const rowIndices = Array.from(selectedDocumentTargetRows);
      const updatedData = removeTargetRows(documentTargetData2D, rowIndices);
      documentTargetData2D = updatedData;
      
      // Update the document extract store
      updateDocumentExtractStore(documentTargetData2D);
      
      // Save to localStorage
      if ($documentExtractFullStore) {
        saveTargetExtract('document', 'document_1', $documentExtractFullStore);
      }
      
      // Clear selection
      selectedDocumentTargetRows.clear();
      
    }
    
    isUpdatingDocumentTarget = false; // Re-enable reactive statements
    documentTargetGridInitialized = true; // Mark document target grid as initialized to prevent reactive statement from running
  }
  
  function handleStudyTargetRemoveRow() {
    isUpdatingStudyTarget = true; // Prevent reactive statement from interfering
    
    if (selectedStudyTargetRows.size > 0) {
      // Use consolidated function from target-table-processing
      const rowIndices = Array.from(selectedStudyTargetRows);
      const updatedData = removeTargetRows(studyTargetData2D, rowIndices);
      studyTargetData2D = updatedData;
      
      // Update the study extract store
      updateStudyExtractStore(studyTargetData2D);
      
      // Save to localStorage
      if ($studyExtractStore) {
        saveTargetExtract('study', 'study_1', $studyExtractStore);
      }
      
      // Clear selection
      selectedStudyTargetRows.clear();
      
    }
    
    isUpdatingStudyTarget = false; // Re-enable reactive statements
    studyTargetGridInitialized = true; // Mark study target grid as initialized to prevent reactive statement from running
  }
  function handleDocumentStudySelected() {
    const selectedRows = getSelectedRowsFromGrid(documentTargetGridApi);
    
    if (selectedRows.size === 0) {
      alert('Please select rows to replace');
      return;
    }
    
    // Filter document data to only include selected rows
    const filteredDocumentData = filterRowsBySelection(documentTargetData2D, selectedRows);
    
    if (filteredDocumentData.length <= 1) {
      alert('No valid document rows selected');
      return;
    }
    
    // For Document→Study, both are using the same project structure keys
    // Build key mapping from target structure (they should be identical)
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    // Use key-based matching that updates existing AND adds new rows
    studyTargetData2D = matchSelectedSyncTargetToTarget(filteredDocumentData, studyTargetData2D, validationResult?.grouperColumns || ['Group'], keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  function handleDocumentStudyMatching() {
    // Build key mapping from target structure
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    studyTargetData2D = matchOnlySyncTargetToTarget(documentTargetData2D, studyTargetData2D, validationResult?.grouperColumns || ['Group'], keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  function handleDocumentStudyAll() {
    // Build key mapping from target structure
    const keyMapping = buildTargetKeyMapping($projectStructureStore, topicId, effectiveTableId, documentTargetData2D[0], referenceId);
    
    studyTargetData2D = matchAllSyncTargetToTarget(documentTargetData2D, studyTargetData2D, keyMapping);
    updateStudyTargetGrid();
    // Update the study extract store to persist changes
    updateStudyExtractStore(studyTargetData2D);
  }
  
  // Pivot functionality moved to SourceTableEditor
  
  // Component mounted
  onMount(() => {
  });
  
  // Cleanup on destroy
  onDestroy(() => {
    // SourceTableEditor manages its own grid cleanup
    if (documentTargetGridInstance) {
      documentTargetGridInstance.destroy();
    }
    if (studyTargetGridInstance) {
      studyTargetGridInstance.destroy();
    }
    // AG-Grid handles its own cleanup
  });
</script>

<!-- Topic Table Editor - Embedded Content -->
<div class="editor-content">
  
  <div class="editor-header">
    <span class="flex items-center gap-5S">
      <div class="editor-header-container">
        <span class="editor-header-label">Table:</span>
        <h2 class="editor-header-title">{tableDisplayName}</h2>
      </div>
      <div class="mode-selection-group">
        <div class="editor-button-group" role="group">
          <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-first {currentMode === 'source-document' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('source-document')}
          >
            Source → Document
          </button>
          <!-- <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-middle {currentMode === 'source-study' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('source-study')}
          >
            Source → Study
          </button> -->
          <button 
            type="button" 
            class="editor-button-group-item editor-button-group-item-last {currentMode === 'document-study' ? 'editor-button-group-item-active' : ''}"
            on:click={() => handleModeChange('document-study')}
          >
            Document → Study
          </button>
        </div>
      </div>
    </span>
    <div class="editor-actions">
      <Button class="save-button" size="xs" color="primary" on:click={handleSaveAndClose}>
        <FontAwesomeIcon icon={faSave} class="mr-2" />
        Save & Close
      </Button>
      <Button class="cancel-button" size="xs" color="light" on:click={handleCancel}>
        <FontAwesomeIcon icon={faTimes} class="mr-2" />
        Cancel
      </Button>
    </div>
  </div>

  <div class="editor-container">
    <!-- SOURCE → DOCUMENT Mode -->
    {#if currentMode === 'source-document'}
      <!-- SOURCE Section -->
      <SourceTableEditor
        availableSourceTables={availableSourceTables}
        bind:selectedSourceTable={selectedSourceTable}
        sourceData2D={sourceData2D}
        workingData={workingData}
        currentTableIndex={currentTableIndex}
        effectiveTableId={effectiveTableId}
        isReferenceMode={isReferenceMode}
        topicId={topicId}
        referenceId={referenceId}
        projectStructureStore={$projectStructureStore}
        columnAddEnabled={columnAddEnabled}
        rowRemoveEnabled={rowRemoveEnabled}
        selectedButtonEnabled={selectedButtonEnabled}
        matchingButtonEnabled={matchingButtonEnabled}
        bind:selectedRows={selectedRows}
        bind:selectedColumns={selectedColumns}
        bind:sourceGridInitialized={sourceGridInitialized}
        isUpdatingCell={isUpdatingCell}
        show={show}
        convertToGridFormat={convertToGridFormat}
        getCurrentVersion={getCurrentVersion}
        updateWorkingDataFromGrid={updateWorkingDataFromGrid}
        runValidation={runValidation}
        onSourceTableChange={(table) => { 
          // Note: selectedSourceTable is already updated via bind:value, but we ensure
          // the reactive statement detects the change by checking if it's different
          if (lastSelectedSourceTable !== table) {
            lastSelectedSourceTable = null; // Force reactive statement to trigger
            selectedSourceTable = table; // Ensure it's set (redundant but safe)
          }
        }}
        onAddColumn={noopWithEvent}
        onAddRow={noop}
        onRemoveRow={noop}
        onPivotTable={noop}
        onResetTable={noop}
        onSaveWorkingData={saveWorkingData}
        onRefreshSourceData={handleRefreshSourceData}
        onMatchSelected={handleMatchSelected}
        onMatchOnly={handleMatchOnly}
        onMatchAll={handleMatchAll}
        onSelectionChanged={handleSourceSelectionChanged}
        onGridReady={handleSourceGridReady}
        onSaveColumn={handleSaveColumn}
        onRemoveColumn={handleRemoveColumn}
      />
      
      <!-- DOCUMENT TARGET Section -->
      <div class="target-section">
        <div class="section-header">
          <span class="section-label">DOCUMENT TARGET</span>
          <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
        </div>
        
        <div class="tool-controls">
          <div class="tool-group">
            <div class="tool-buttons">
              <Button class="tool-button" size="xs" color="light" title="Add Row" on:click={handleTargetAddRow}>
                <CustomImage id="table-row-add" size={24} />
              </Button>
              <Button class="tool-button" size="xs" color="light" title="Remove Row" disabled={!documentTargetRowRemoveEnabled} on:click={handleTargetRemoveRow}>
                <CustomImage id="table-row-remove" size={24} />
              </Button>
            </div>
          </div>
          
          <div class="tool-group">
            <div class="tool-buttons">
              <!-- Reset Table button hidden for now -->
              <!-- <Button size="xs" color="primary" on:click={handleTargetTableReset}>Reset Table</Button> -->
            </div>
          </div>
        </div>
        
        <div class="grid-wrapper">
          {#if documentTargetTableStructure && documentTargetTableData}
            <TopicGrid
              columns={documentTargetTableStructure.columns || []}
              rows={documentTargetTableData.rows || []}
              topicId={topicId}
              tableId={effectiveTableId}
              showColumnKeys={true}
              grouperData={documentTargetGrouperData}
              showIndicators={true}
              domLayout="normal"
              onCellValueChanged={handleDocumentTargetCellValueChanged}
            />
          {/if}
        </div>
      </div>

    {/if}

    <!-- SOURCE → STUDY Mode -->
    {#if currentMode === 'source-study'}
      <!-- SOURCE Section -->
      <SourceTableEditor
        availableSourceTables={availableSourceTables}
        bind:selectedSourceTable={selectedSourceTable}
        sourceData2D={sourceData2D}
        workingData={workingData}
        currentTableIndex={currentTableIndex}
        effectiveTableId={effectiveTableId}
        isReferenceMode={isReferenceMode}
        topicId={topicId}
        referenceId={referenceId}
        projectStructureStore={$projectStructureStore}
        columnAddEnabled={columnAddEnabled}
        rowRemoveEnabled={rowRemoveEnabled}
        selectedButtonEnabled={selectedButtonEnabled}
        matchingButtonEnabled={matchingButtonEnabled}
        bind:selectedRows={selectedRows}
        bind:selectedColumns={selectedColumns}
        bind:sourceGridInitialized={sourceGridInitialized}
        isUpdatingCell={isUpdatingCell}
        show={show}
        convertToGridFormat={convertToGridFormat}
        getCurrentVersion={getCurrentVersion}
        updateWorkingDataFromGrid={updateWorkingDataFromGrid}
        runValidation={runValidation}
        onSourceTableChange={(table) => { 
          // Note: selectedSourceTable is already updated via bind:value, but we ensure
          // the reactive statement detects the change by checking if it's different
          if (lastSelectedSourceTable !== table) {
            lastSelectedSourceTable = null; // Force reactive statement to trigger
            selectedSourceTable = table; // Ensure it's set (redundant but safe)
          }
        }}
        onAddColumn={noopWithEvent}
        onAddRow={noop}
        onRemoveRow={noop}
        onPivotTable={noop}
        onResetTable={noop}
        onSaveWorkingData={saveWorkingData}
        onRefreshSourceData={handleRefreshSourceData}
        onMatchSelected={handleMatchSelected}
        onMatchOnly={handleMatchOnly}
        onMatchAll={handleMatchAll}
        onSelectionChanged={handleSourceSelectionChanged}
        onGridReady={handleSourceGridReady}
        onSaveColumn={handleSaveColumn}
        onRemoveColumn={handleRemoveColumn}
      />
   
      <!-- STUDY TARGET Section -->
      <div class="target-section">
        <div class="section-header">
              <span class="section-label">STUDY TARGET:</span>
          <span class="table-name">{tableId.toUpperCase()}</span>
        </div>
        
        <div class="tool-controls">
          <div class="tool-group">
            <div class="tool-buttons">
              <Button size="xs" color="primary" on:click={handleTargetAddRow} title="Add Row">
                <CustomImage id="table-row-add" size={24} />
              </Button>
              <Button size="xs" color="primary" disabled={!studyTargetRowRemoveEnabled} on:click={handleTargetRemoveRow} title="Remove Row">
                <CustomImage id="table-row-remove" size={24} />
              </Button>
            </div>
          </div>
          
          <div class="tool-group">
            <div class="tool-buttons">
                  <!-- Reset Table button hidden for now -->
                <!-- <Button size="xs" color="primary" on:click={handleTargetTableReset}>Reset Table</Button> -->
            </div>
          </div>
        </div>
        
        <div class="grid-wrapper">
          {#if studyTargetTableStructure && studyTargetTableData}
              <TopicGrid
                columns={studyTargetTableStructure.columns || []}
                rows={studyTargetTableData.rows || []}
                topicId={topicId}
                tableId={effectiveTableId}
                showColumnKeys={true}
                grouperData={studyTargetGrouperData}
                showIndicators={true}
                domLayout="normal"
                getSourceInfoForCell={getSourceInfoForCell}
                onCellValueChanged={handleStudyTargetCellValueChanged}
              />
          {/if}
        </div>
      </div>
    {/if}

    <!-- DOCUMENT → STUDY Mode -->
    {#if currentMode === 'document-study'}
      <div class="document-study-container">
        <!-- DOCUMENT TARGET Section -->
        <div class="target-section">
          <div class="section-header">
            <span class="section-label">DOCUMENT TARGET</span>
            <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
          </div>
          
          <div class="tool-controls">
            <div class="tool-group">
              <div class="tool-buttons">
                <Button class="tool-button" size="xs" color="light" title="Add Row" on:click={handleDocumentTargetAddRow}>
                  <CustomImage id="table-row-add" size={24} />
                </Button>
                <Button class="tool-button" size="xs" color="light" title="Remove Row" disabled={!documentTargetRowRemoveEnabled} on:click={handleDocumentTargetRemoveRow}>
                  <CustomImage id="table-row-remove" size={24} />
                </Button>
              </div>
            </div>
            
            <div class="tool-group">
              <div class="tool-buttons">
                <!-- Reset Table button hidden for now -->
                <!-- <Button size="xs" color="primary" on:click={handleTargetTableReset}>Reset Table</Button> -->
              </div>
            </div>
          </div>
          
          <div class="grid-wrapper">
            {#if documentTargetTableStructure && documentTargetTableData}
              <TopicGrid
                columns={documentTargetTableStructure.columns || []}
                rows={documentTargetTableData.rows || []}
                topicId={topicId}
                tableId={effectiveTableId}
                showColumnKeys={true}
                grouperData={documentTargetGrouperData}
                showIndicators={true}
                domLayout="normal"
                onCellValueChanged={handleDocumentTargetCellValueChanged}
              />
            {/if}
          </div>
        </div>
        
        <!-- Replace Action Area -->
        <div class="replace-actions">
          <span class="replace-label">Replace:</span>
          <div class="replace-buttons">
            <Button size="xs" color="primary" disabled={!documentStudySelectedButtonEnabled} on:click={handleDocumentStudySelected}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              Selected
            </Button>
            <span class="replace-label">OR</span>           
            <Button size="xs" color="primary" on:click={handleDocumentStudyMatching}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              Matching
            </Button>
            <span class="replace-label">OR</span>           
            <Button size="xs" color="primary" on:click={handleDocumentStudyAll}>
              <FontAwesomeIcon icon={faArrowDown} class="mr-2" />
              All
            </Button>
          </div>
        </div>
        
        <!-- STUDY TARGET Section -->
        <div class="target-section">
          <div class="section-header">
            <span class="section-label">STUDY TARGET</span>
            <!-- <span class="table-name">{effectiveTableId.toUpperCase()}</span> -->
          </div>
   
          <div class="tool-controls">
            <!-- <div class="tool-group">
              <div class="tool-buttons">
                <Button class="tool-button" size="xs" color="light" title="Add Row" on:click={handleStudyTargetAddRow}><CustomImage id="table-row-add" size={24} /></Button>
                <Button class="tool-button" size="xs" color="light" title="Remove Row" disabled={!studyTargetRowRemoveEnabled} on:click={handleStudyTargetRemoveRow}><CustomImage id="table-row-remove" size={24} /></Button>
              </div>
            </div> -->
            
            <div class="tool-group">
              <div class="tool-buttons">
                <Button class="tool-button" size="xs" color="light" title="Reset Table"><FontAwesomeIcon icon={faRotateLeft} size="sm" /></Button>
              </div>
            </div>
          </div>
          
          <div class="grid-wrapper">
            {#if studyTargetTableStructure && studyTargetTableData}
                <TopicGrid
                  columns={studyTargetTableStructure.columns || []}
                  rows={studyTargetTableData.rows || []}
                  topicId={topicId}
                  tableId={effectiveTableId}
                  showColumnKeys={true}
                  grouperData={studyTargetGrouperData}
                  showIndicators={true}
                  domLayout="normal"
                  getSourceInfoForCell={getSourceInfoForCell}
                  onCellValueChanged={handleStudyTargetCellValueChanged}
                />
            {/if}
          </div>
        </div>
      </div>
    {/if}
    
  </div>

</div>
